import { supabase } from './supabase'

/**
 * NatuCafé (restaurant_id=19) — Crea un print job de cocina con
 * SOLO los items recién adicionados a una mesa que YA tenía orden
 * abierta y comanda inicial impresa.
 *
 * Diseño:
 * - El bridge.py de NatuCafé, al procesar un print job de cocina,
 *   intenta fetch LIVE de order_items usando `payload.order_db_id`
 *   o `payload.order_id` (ver `_natucafe_pick_order_id`). Si AMBOS
 *   están ausentes, cae al fallback `payload.items` — exactamente
 *   los items que le pasamos acá. Eso nos permite imprimir SOLO
 *   los items nuevos sin tocar el bridge.
 * - Trigger único por llamada (`add_items_<ts>_<rand>`) evita
 *   colisionar con el UNIQUE INDEX `idx_order_print_jobs_natucafe_dedupe`
 *   (que indexa por (order_id, payload->>'trigger')).
 * - Mesa label resuelve sola en el bridge desde `table_id`.
 * - Split BEBIDAS/COMIDAS sigue funcionando porque cada item lleva
 *   su `station_code`.
 *
 * NO BLOQUEANTE: cualquier error se loguea y NO interrumpe el flujo
 * del usuario. La orden ya fue persistida correctamente; solo la
 * impresión podría fallar (impresora apagada, bridge caído, etc.).
 */
export interface AdditionItemInput {
  menu_item_id: string | null
  item_name_snapshot: string
  unit_price_snapshot: number
  quantity: number
  notes?: string | null
  line_total?: number | null
  /** Si no se pasa, se intenta resolver desde menu_items.station_code */
  station_code?: string | null
  /** Opciones seleccionadas (cada una con option_group, option_name, price_delta). */
  selected_options?: Array<{
    option_group?: string | null
    option_name?: string | null
    price_delta?: number | null
  }>
}

export async function createNatuCafeAdditionPrintJob(params: {
  restaurantId: number | string | undefined | null
  orderId: string | number | null | undefined
  addedItems: AdditionItemInput[]
}): Promise<{ ok: boolean; jobId?: string; reason?: string }> {
  const { restaurantId, orderId, addedItems } = params

  // Guard 1 — solo NatuCafé (id 19). Cualquier otro restaurante: no-op.
  if (String(restaurantId ?? '') !== '19') {
    return { ok: false, reason: 'not_natucafe_skip' }
  }
  if (!orderId) {
    return { ok: false, reason: 'no_order_id' }
  }
  if (!addedItems || addedItems.length === 0) {
    return { ok: false, reason: 'no_items' }
  }

  try {
    // 1) Fetch metadata de la orden — necesitamos tracking_token (UUID
    //    para la columna order_print_jobs.order_id) y table_id (para
    //    que el bridge resuelva 'Mesa N' / 'Barra5').
    const { data: order, error: orderErr } = await supabase
      .from('orders')
      .select(
        'id, tracking_token, order_number, table_id, customer_name, customer_phone, delivery_type, notes',
      )
      .eq('id', orderId)
      .maybeSingle()
    if (orderErr) {
      console.warn('[NATUCAFE_ADD_PRINT] order fetch error:', orderErr)
    }

    // 2) Resolver station_code faltante via menu_items. Sin station_code
    //    el split BEBIDAS/COMIDAS del bridge tira todo a COMIDAS por
    //    default — que es seguro pero queremos precisión.
    const itemsNeedingStation = addedItems.filter(
      (it) => it.station_code == null && it.menu_item_id != null,
    )
    const stationMap = new Map<string, string | null>()
    if (itemsNeedingStation.length > 0) {
      const ids = itemsNeedingStation
        .map((it) => it.menu_item_id!)
        .filter(Boolean)
      try {
        const { data: miRows } = await supabase
          .from('menu_items')
          .select('id, station_code')
          .in('id', ids)
        ;(miRows || []).forEach((r: any) => {
          stationMap.set(
            String(r.id),
            (r.station_code as string | null) ?? null,
          )
        })
      } catch (ex) {
        console.warn(
          '[NATUCAFE_ADD_PRINT] menu_items station_code fetch failed:',
          ex,
        )
      }
    }

    // 3) Construir el payload.items para el bridge.
    const itemsPayload = addedItems.map((it) => {
      const qty = Number(it.quantity) || 1
      const unit = Number(it.unit_price_snapshot) || 0
      const lineTotal =
        it.line_total != null && !Number.isNaN(Number(it.line_total))
          ? Number(it.line_total)
          : unit * qty
      const resolvedStation =
        it.station_code != null
          ? it.station_code
          : it.menu_item_id
            ? (stationMap.get(String(it.menu_item_id)) ?? null)
            : null
      return {
        menu_item_id: it.menu_item_id ?? null,
        item_name_snapshot: it.item_name_snapshot,
        unit_price_snapshot: unit,
        quantity: qty,
        notes: it.notes ?? null,
        line_total: lineTotal,
        station_code: resolvedStation,
        selected_options: (it.selected_options || []).map((o) => ({
          option_group: o?.option_group ?? '',
          option_name: o?.option_name ?? '',
          price_delta: Number(o?.price_delta) || 0,
        })),
      }
    })

    // 4) Trigger único para este print job. NO colisiona con el
    //    UNIQUE INDEX idx_order_print_jobs_natucafe_dedupe (que dedupea
    //    por (order_id, payload->>'trigger'), y la comanda inicial usa
    //    trigger='natucafe_new').
    const triggerValue = `add_items_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`

    // 5) Payload del bridge.
    //    CRÍTICO: NO incluir `order_id` ni `order_db_id` dentro del
    //    payload, así `_natucafe_pick_order_id` retorna None y el
    //    bridge usa `payload.items` directamente — sólo los items
    //    recién agregados, no la orden completa.
    const innerPayload: Record<string, any> = {
      trigger: triggerValue,
      order_number: order?.order_number ?? null,
      customer_name: order?.customer_name ?? null,
      customer_phone: order?.customer_phone ?? null,
      delivery_type: order?.delivery_type ?? 'dine_in',
      table_id: order?.table_id ?? null,
      // notes='ADICIÓN' hace que el bridge imprima un bloque
      // 'NOTA: ADICIÓN' al final del ticket — señal visual clara
      // para cocina/barra de que NO es la comanda original.
      notes: 'ADICIÓN',
      created_at: new Date().toISOString(),
      items: itemsPayload,
      is_addition: true,
    }

    const insertRow = {
      restaurant_id: 19,
      // La columna order_print_jobs.order_id es UUID. Usamos
      // tracking_token (UUID) de la orden. Si por algún motivo no
      // existe, mandamos NULL — el bridge igual procesa el job (no
      // hace lookup por order_id row column).
      order_id: order?.tracking_token || null,
      order_number: order?.order_number ?? null,
      status: 'pending' as const,
      target_printer_type: 'kitchen' as const,
      payload: innerPayload,
    }

    const { data: inserted, error: insertErr } = await supabase
      .from('order_print_jobs')
      .insert(insertRow)
      .select('id')
      .maybeSingle()

    if (insertErr) {
      console.warn('[NATUCAFE_ADD_PRINT] insert failed:', insertErr)
      return { ok: false, reason: insertErr.message || 'insert_error' }
    }

    return { ok: true, jobId: (inserted as any)?.id }
  } catch (ex: any) {
    console.warn('[NATUCAFE_ADD_PRINT] unexpected error:', ex)
    return { ok: false, reason: ex?.message || 'exception' }
  }
}
